import { createApi } from '@reduxjs/toolkit/query/react'
import { Model } from '../types/models/model'
import { MODELS_URL } from '../constants'
import baseQueryWithReauth from './baseQuery'
import { ListModelResp, QueryModelResp } from '../types/api/responses'
import { RunInferenceResponse } from '../types/prediction'
import { apiTags } from './apiTags'
import { ModelCreateParams, ModelUpdateParams, QueryModelReq } from '../types/api/api-model'

export const modelsApi = createApi({
  reducerPath: 'modelsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [apiTags.Model, apiTags.ModelList],

  endpoints: (builder) => ({
    fetchModels: builder.query<
      ListModelResp,
      {
        project_id: string
        limit?: number
        page?: number
        sort_key?: string
        sort_val?: number
      }
    >({
      query: (params) => ({
        url: MODELS_URL,
        params,
      }),
      providesTags: (result) => [
        { type: apiTags.ModelList, id: 'LIST' },
        ...(result?.models?.map(({ id }) => ({
          type: apiTags.Model,
          id,
        })) || []),
      ],
      keepUnusedDataFor: 0,
    }),

    createModel: builder.mutation<Model, ModelCreateParams>({
      query: (modelData) => ({
        url: MODELS_URL,
        method: 'POST',
        body: modelData,
      }),
      invalidatesTags: (result) => [
        { type: apiTags.ModelList, id: 'LIST' },
        ...(result?.id ? [{ type: apiTags.Model, id: result.id }] : []),
      ],
    }),

    getModel: builder.query<Model, string>({
      query: (id) => `${MODELS_URL}/${id}`,
      providesTags: (result, error, id) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
      ],
      keepUnusedDataFor: 0,
      // Handle 404 errors gracefully - don't retry for deleted models
      transformErrorResponse: (response, meta, arg) => {
        if (response.status === 404) {
          // Return a more user-friendly error for 404s
          return { ...response, data: { message: 'Model not found or deleted' } }
        }
        return response
      },
    }),

    updateModel: builder.mutation<
      Model,
      {
        id: string
        modelData: ModelUpdateParams
      }
    >({
      query: ({ id, modelData }) => ({
        url: `${MODELS_URL}/${id}`,
        method: 'PATCH',
        body: modelData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
      ],
    }),

    deleteModel: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `${MODELS_URL}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.ModelList, id: 'LIST' },
        { type: apiTags.Model, id },
      ],
    }),

    deployModel: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `${MODELS_URL}/${id}/deploy`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
      ],
    }),

    deleteModelDeployment: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `${MODELS_URL}/${id}/deploy`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
      ],
    }),

    runBatchInference: builder.mutation<
      { message: string },
      {
        id: string
        project_id: string
        dataset_id: string
        thumbnail_size?: number
        metadata_filter: { datasource: string[] } | undefined
        threshold?: number
      }
    >({
      query: ({ id, thumbnail_size, metadata_filter, threshold }) => ({
        url: `${MODELS_URL}/${id}/inference/batch`,
        method: 'POST',
        body: { thumbnail_size, metadata_filter, threshold },
      }),
      invalidatesTags: (result, error, { id, dataset_id, project_id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
        { type: apiTags.PredictionList, id: 'LIST' },
        { type: apiTags.PredictionStats, id: id },
        { type: apiTags.Content, id: 'LIST' },
        { type: apiTags.Metadata, id: project_id },
        { type: apiTags.Annotation, id: 'LIST' },
        { type: apiTags.AnnotationStats, id: dataset_id },
        { type: apiTags.AnnotationStats, id: 'LIST' },
        { type: apiTags.Dataset, id: dataset_id },
        { type: apiTags.DatasetList, id: 'LIST' },
      ],
    }),

    runRealtimeInference: builder.mutation<
      RunInferenceResponse,
      {
        id: string
        data: FormData | Blob
        threshold?: number
        heatmap?: boolean
      }
    >({
      query: ({ id, data, threshold, heatmap }) => ({
        url: `${MODELS_URL}/${id}/inference/realtime`,
        method: 'POST',
        body: data,
        params: { threshold: threshold || 0.5, heatmap },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
        { type: apiTags.PredictionList, id: 'LIST' },
        { type: apiTags.PredictionStats, id },
      ],
    }),

    trainModel: builder.mutation<
      Model,
      {
        id: string
        train_metadata_filter: { datasource: string[] } | undefined
        validation_metadata_filter: { datasource: string[] } | undefined
        tag_filter?: string[] | undefined
      }
    >({
      query: ({ id, train_metadata_filter, validation_metadata_filter, tag_filter }) => ({
        url: `${MODELS_URL}/${id}/train`,
        method: 'POST',
        body: { train_metadata_filter, validation_metadata_filter, tag_filter },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
        { type: apiTags.PredictionList, id: 'LIST' },
        { type: apiTags.PredictionStats, id },
      ],
    }),
    fineTuneModel: builder.mutation<
      Model,
      {
        name: string
        train_metadata_filter: { datasource: string[] } | undefined
        validation_metadata_filter: { datasource: string[] } | undefined
        tag_filter?: string[] | undefined
        id: string
        epochs: number
        freeze: number
      }
    >({
      query: ({
        id,
        epochs,
        freeze,
        name,
        train_metadata_filter,
        validation_metadata_filter,
        tag_filter,
      }) => ({
        url: `${MODELS_URL}/${id}/fineTune`,
        method: 'POST',
        body: {
          epochs,
          freeze,
          name,
          train_metadata_filter,
          validation_metadata_filter,
          tag_filter,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
        { type: apiTags.PredictionList, id: 'LIST' },
        { type: apiTags.PredictionStats, id },
      ],
    }),

    stopTrainModel: builder.mutation<
      Model,
      {
        id: string
      }
    >({
      query: ({ id }) => ({
        url: `${MODELS_URL}/${id}/train/stop`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
        { type: apiTags.PredictionList, id: 'LIST' },
        { type: apiTags.PredictionStats, id },
      ],
    }),

    queryModels: builder.query<QueryModelResp, QueryModelReq>({
      query: (queryData) => ({
        url: `${MODELS_URL}/query`,
        method: 'POST',
        body: queryData,
      }),
      providesTags: (result) => [
        { type: apiTags.ModelList, id: 'LIST' },
        ...(result?.models?.map(({ id }) => ({
          type: apiTags.Model,
          id,
        })) || []),
      ],
      keepUnusedDataFor: 0,
    }),

    getDefaultHyperparameters: builder.query<any, string>({
      query: (id) => ({
        url: `${MODELS_URL}/${id}/hyperparameters`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: apiTags.Model, id }],
    }),

    stopBatchInference: builder.mutation<
      { message: string },
      {
        id: string
      }
    >({
      query: ({ id }) => ({
        url: `${MODELS_URL}/${id}/inference/batch/stop`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
        { type: apiTags.PredictionList, id: 'LIST' },
        { type: apiTags.PredictionStats, id },
      ],
    }),

    runRealtimeTiffInference: builder.mutation<
      { message: string },
      {
        id: string
        data: FormData | Blob
        threshold?: number
        target_gsd: number
        tiff_gsd: number
      }
    >({
      query: ({ id, data, threshold = 0.85, target_gsd, tiff_gsd }) => ({
        url: `${MODELS_URL}/${id}/inference/realtime/tiff`,
        method: 'POST',
        body: data,
        params: { threshold, target_gsd, tiff_gsd },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Model, id },
        { type: apiTags.ModelList, id: 'LIST' },
        { type: apiTags.PredictionList, id: 'LIST' },
        { type: apiTags.PredictionStats, id },
      ],
    }),
  }),
})

export const {
  useFetchModelsQuery,
  useCreateModelMutation,
  useGetModelQuery,
  useUpdateModelMutation,
  useDeleteModelMutation,
  useDeployModelMutation,
  useDeleteModelDeploymentMutation,
  useRunBatchInferenceMutation,
  useRunRealtimeInferenceMutation,
  useTrainModelMutation,
  useFineTuneModelMutation,
  useStopTrainModelMutation,
  useQueryModelsQuery,
  useGetDefaultHyperparametersQuery,
  useStopBatchInferenceMutation,
  useRunRealtimeTiffInferenceMutation,
} = modelsApi

export const {
  util: { invalidateTags },
} = modelsApi
